import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Grid, Alert, CircularProgress,
  Avatar, TextField, MenuItem, Tooltip, IconButton, Tab, Tabs,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  LinearProgress,
} from '@mui/material';
import { School, Refresh, TrendingUp } from '@mui/icons-material';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const YEAR = new Date().getFullYear();

function apiFetch(path: string) {
  return fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const CAT_COLORS: Record<string, string> = {
  compliance:  '#b71c1c',
  technical:   '#1565c0',
  soft_skills: '#6a1b9a',
  safety:      '#e65100',
  onboarding:  '#2e7d32',
  leadership:  '#C08A2E',
  other:       '#546e7a',
};

function KpiCard({ label, value, sub, color = '#37474f' }: any) {
  return (
    <Paper sx={{ p: 2, borderTop: `3px solid ${color}` }}>
      <Typography variant="h5" fontWeight={700} color={color}>{value ?? '—'}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      {sub && <Typography variant="caption" display="block" color="text.disabled">{sub}</Typography>}
    </Paper>
  );
}

function CategoryChip({ cat }: { cat: string }) {
  const color = CAT_COLORS[cat] ?? '#546e7a';
  const label = cat.split('_').map((w: string) => w[0].toUpperCase() + w.slice(1)).join(' ');
  return <Chip size="small" label={label} sx={{ bgcolor: color + '18', color, fontSize: '0.6rem' }} />;
}

function BarCell({ value, max = 100, color = '#C08A2E' }: { value: number; max?: number; color?: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <Box sx={{ minWidth: 80 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
        <Typography variant="caption" fontWeight={600}>{value}%</Typography>
      </Box>
      <LinearProgress variant="determinate" value={Math.min(pct, 100)}
        sx={{ height: 6, borderRadius: 3,
          '& .MuiLinearProgress-bar': { bgcolor: value >= 80 ? '#2e7d32' : value >= 50 ? '#f57f17' : '#b71c1c' } }} />
    </Box>
  );
}

export default function TrainingRoi() {
  const [year, setYear]       = useState(YEAR);
  const [tab, setTab]         = useState(0);
  const [data, setData]       = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await apiFetch(`/api/training-roi?year=${year}`)); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const spend      = data?.spend ?? {};
  const completion = data?.completion ?? {};
  const perf       = data?.perfImpact ?? [];
  const courses    = data?.courseRoi ?? [];
  const depts      = data?.deptBreakdown ?? [];
  const mandatory  = data?.mandatory ?? [];
  const trend      = data?.monthlyTrend ?? [];
  const top        = data?.topCourses ?? [];

  const trainedCohort    = perf.find((c: any) => c.cohort === 'trained');
  const notTrainedCohort = perf.find((c: any) => c.cohort === 'not_trained');
  const perfLift = trainedCohort && notTrainedCohort
    ? (parseFloat(trainedCohort.avgPerfScore) - parseFloat(notTrainedCohort.avgPerfScore)).toFixed(1)
    : null;

  const lowCompliance = mandatory.filter((m: any) => parseFloat(m.complianceRate) < 80);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#1565c0', width: 48, height: 48 }}><School /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Training ROI Analysis</Typography>
            <Typography variant="body2" color="text.secondary">
              Learning investment, completion rates, and impact on performance
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField select size="small" label="Year" value={year}
            onChange={e => setYear(parseInt(e.target.value))} sx={{ width: 90 }}>
            {[YEAR - 1, YEAR, YEAR + 1].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
          <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {lowCompliance.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{lowCompliance.length} mandatory course{lowCompliance.length > 1 ? 's' : ''} below 80% compliance:</strong>{' '}
          {lowCompliance.map((m: any) => m.name).join(', ')}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* KPI row */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KpiCard label="Total Spend" value={spend.totalSpent ? `HK$${parseFloat(spend.totalSpent).toLocaleString('en-HK', { maximumFractionDigits: 0 })}` : '—'} color="#C08A2E" />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KpiCard label="Completion Rate" value={completion.completionRate ? `${completion.completionRate}%` : '—'} color={parseFloat(completion.completionRate) >= 80 ? '#2e7d32' : '#e65100'} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KpiCard label="Unique Learners" value={spend.uniqueLearners ?? '—'} color="#1565c0" />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KpiCard label="Total Enrollments" value={completion.total ?? '—'} color="#37474f" />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KpiCard label="Avg Score" value={completion.avgScore ? `${completion.avgScore}%` : '—'} color="#6a1b9a" />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KpiCard label="Training Hours" value={spend.totalTrainingHours ? Math.round(parseFloat(spend.totalTrainingHours)) : '—'} sub="hours completed" color="#00838f" />
            </Grid>
          </Grid>

          {/* Performance impact banner */}
          {trainedCohort && notTrainedCohort && (
            <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center',
              borderLeft: `4px solid ${parseFloat(perfLift ?? '0') > 0 ? '#2e7d32' : '#e65100'}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp sx={{ color: '#2e7d32' }} />
                <Typography fontWeight={700} color="#2e7d32">
                  +{perfLift} avg performance score lift
                </Typography>
              </Box>
              <Typography variant="body2">
                Trained employees avg <strong>{parseFloat(trainedCohort.avgPerfScore).toFixed(1)}</strong> vs{' '}
                untrained <strong>{parseFloat(notTrainedCohort.avgPerfScore).toFixed(1)}</strong>
              </Typography>
              <Typography variant="body2">
                Promotion rate: trained <strong>{trainedCohort.promotionRate}%</strong> vs untrained <strong>{notTrainedCohort.promotionRate}%</strong>
              </Typography>
            </Paper>
          )}

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none' } }}>
            <Tab label={`Course ROI (${courses.length})`} />
            <Tab label="By Department" />
            <Tab label={`Mandatory Compliance (${mandatory.length})`} />
            <Tab label="Monthly Trend" />
          </Tabs>

          {tab === 0 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Course</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Enrolments</TableCell>
                    <TableCell align="right">Completions</TableCell>
                    <TableCell>Completion Rate</TableCell>
                    <TableCell align="right">Avg Score</TableCell>
                    <TableCell align="right">Total Spend</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {courses.map((c: any) => (
                    <TableRow key={c.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                        {c.isMandatory && <Chip size="small" label="Mandatory" sx={{ bgcolor: '#ffebee', color: '#b71c1c', fontSize: '0.55rem', height: 16 }} />}
                      </TableCell>
                      <TableCell><CategoryChip cat={c.category} /></TableCell>
                      <TableCell align="right">{c.enrollments}</TableCell>
                      <TableCell align="right">{c.completions}</TableCell>
                      <TableCell><BarCell value={parseFloat(c.completionRate ?? 0)} /></TableCell>
                      <TableCell align="right">
                        {c.avgScore
                          ? <Chip size="small" label={`${parseFloat(c.avgScore).toFixed(0)}%`}
                              sx={{ bgcolor: parseFloat(c.avgScore) >= 80 ? '#e8f5e9' : '#fff3e0',
                                color: parseFloat(c.avgScore) >= 80 ? '#2e7d32' : '#e65100', fontWeight: 700 }} />
                          : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(c.totalSpent) > 0
                          ? <Typography variant="body2" fontWeight={600} color="#C08A2E">
                              HK${parseFloat(c.totalSpent).toLocaleString('en-HK', { maximumFractionDigits: 0 })}
                            </Typography>
                          : <Chip size="small" label="Internal" sx={{ bgcolor: '#f5f5f5', fontSize: '0.6rem' }} />
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                  {courses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No course data for {year}.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 1 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Department</TableCell>
                    <TableCell align="right">Learners</TableCell>
                    <TableCell align="right">Headcount</TableCell>
                    <TableCell>Participation</TableCell>
                    <TableCell>Completion Rate</TableCell>
                    <TableCell align="right">Avg Hours</TableCell>
                    <TableCell align="right">Spend</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {depts.filter((d: any) => d.department).map((d: any) => {
                    const participation = d.headcount > 0 ? Math.round((d.learners / d.headcount) * 100) : 0;
                    return (
                      <TableRow key={d.department} hover>
                        <TableCell><Typography variant="body2" fontWeight={600}>{d.department}</Typography></TableCell>
                        <TableCell align="right">{d.learners ?? 0}</TableCell>
                        <TableCell align="right">{d.headcount}</TableCell>
                        <TableCell><BarCell value={participation} /></TableCell>
                        <TableCell><BarCell value={parseFloat(d.completionRate ?? 0)} /></TableCell>
                        <TableCell align="right">{d.avgHours ? `${parseFloat(d.avgHours).toFixed(1)}h` : '—'}</TableCell>
                        <TableCell align="right">
                          {d.spend > 0
                            ? <Typography variant="body2" fontWeight={600} color="#C08A2E">
                                HK${parseFloat(d.spend).toLocaleString('en-HK', { maximumFractionDigits: 0 })}
                              </Typography>
                            : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 2 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Course</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Total Employees</TableCell>
                    <TableCell align="right">Completed</TableCell>
                    <TableCell>Compliance Rate</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mandatory.map((m: any) => {
                    const rate = parseFloat(m.complianceRate ?? 0);
                    return (
                      <TableRow key={m.id} hover sx={{ bgcolor: rate < 80 ? '#fff8f8' : undefined }}>
                        <TableCell><Typography variant="body2" fontWeight={600}>{m.name}</Typography></TableCell>
                        <TableCell><CategoryChip cat={m.category} /></TableCell>
                        <TableCell align="right">{m.totalEmployees}</TableCell>
                        <TableCell align="right">{m.completed}</TableCell>
                        <TableCell><BarCell value={rate} /></TableCell>
                        <TableCell>
                          <Chip size="small"
                            label={rate >= 80 ? '✅ Compliant' : rate >= 50 ? '⚠️ At Risk' : '❌ Non-compliant'}
                            sx={{
                              bgcolor: rate >= 80 ? '#e8f5e9' : rate >= 50 ? '#fffde7' : '#ffebee',
                              color:   rate >= 80 ? '#2e7d32' : rate >= 50 ? '#f57f17' : '#b71c1c',
                              fontWeight: 700, fontSize: '0.6rem',
                            }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {mandatory.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No mandatory courses configured.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 3 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Monthly Enrolments vs Completions ({year})</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end', height: 120, mb: 1 }}>
                {trend.map((t: any) => {
                  const maxVal = Math.max(...trend.map((x: any) => Math.max(x.enrollments ?? 0, x.completions ?? 0)), 1);
                  const enH  = Math.max(4, ((t.enrollments ?? 0) / maxVal) * 110);
                  const comH = Math.max(4, ((t.completions ?? 0) / maxVal) * 110);
                  return (
                    <Box key={t.month} sx={{ flex: 1, display: 'flex', gap: 0.25, alignItems: 'flex-end', justifyContent: 'center' }}>
                      <Tooltip title={`${t.monthLabel}: ${t.enrollments ?? 0} enrolments`}>
                        <Box sx={{ flex: 1, height: enH, bgcolor: '#1565c0', opacity: 0.7, borderRadius: '2px 2px 0 0' }} />
                      </Tooltip>
                      <Tooltip title={`${t.monthLabel}: ${t.completions ?? 0} completions`}>
                        <Box sx={{ flex: 1, height: comH, bgcolor: '#2e7d32', opacity: 0.8, borderRadius: '2px 2px 0 0' }} />
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
                <Typography variant="caption"><Box component="span" sx={{ bgcolor: '#1565c0', px: 1, mr: 0.5, borderRadius: 0.5 }} />Enrolments</Typography>
                <Typography variant="caption"><Box component="span" sx={{ bgcolor: '#2e7d32', px: 1, mr: 0.5, borderRadius: 0.5 }} />Completions</Typography>
              </Box>
              {top.length > 0 && (
                <>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ mt: 1 }}>Top Courses by Completions</Typography>
                  {top.map((c: any) => (
                    <Box key={c.name} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CategoryChip cat={c.category} />
                      <Typography variant="body2" sx={{ flex: 1 }}>{c.name}</Typography>
                      <Chip size="small" label={`${c.completions} done`}
                        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: '0.65rem' }} />
                      {c.avgScore && <Chip size="small" label={`${parseFloat(c.avgScore).toFixed(0)}%`}
                        sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700, fontSize: '0.65rem' }} />}
                    </Box>
                  ))}
                </>
              )}
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}
